import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { BookOpen, Brain, Eye, Users, Shield, Network, Landmark, Cpu, Compass, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { CURRICULUM, getOverallProgress, getModuleProgress, getTotalLessons } from '@/lib/curriculum';
import { useProgress } from '@/hooks/useProgress';
import { cn } from '@/lib/utils';

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Eye, Brain, Users, Shield, Network, Landmark, Cpu, Compass,
};

const Index = () => {
  useSeoMeta({
    title: 'Reality Architect — A Curriculum in How to Understand Everything',
    description: '74 lessons across 8 modules. A micro-learning curriculum synthesizing philosophy, cognitive science, political economy, systems thinking, and contemplative practice.',
  });

  const { progress } = useProgress();
  const overall = getOverallProgress(progress);
  const totalLessons = getTotalLessons();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      {/* Hero */}
      <header className="relative overflow-hidden border-b bg-white dark:bg-slate-950">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-100/40 via-transparent to-transparent dark:from-purple-900/10" />
        <div className="container relative py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="mb-4 gap-1.5">
              <Sparkles className="size-3" />
              MICRO-LEARNING SYLLABUS — Definitive Edition v3.0
            </Badge>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl lg:text-6xl">
              Understanding{' '}
              <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-rose-500 bg-clip-text text-transparent">
                Reality&apos;s Architecture
              </span>
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-600 dark:text-slate-400">
              A comprehensive curriculum exploring how we know, why we believe, how power constructs truth, and what it means to navigate the polycrisis with clarity and care.
            </p>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link
                to={progress.currentModule && progress.currentLesson
                  ? `/module/${progress.currentModule}/lesson/${progress.currentLesson}`
                  : '/module/m1'}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                <BookOpen className="size-4" />
                {overall.completed > 0 ? 'Continue Learning' : 'Begin the Journey'}
                <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/chat"
                className="inline-flex items-center gap-2 rounded-lg border px-6 py-3 text-sm font-medium transition hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900"
              >
                <Sparkles className="size-4" />
                Ask the AI Tutor
              </Link>
            </div>
            {/* Overall Progress */}
            <div className="mx-auto mt-10 max-w-md">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Overall Progress</span>
                <span className="font-mono font-medium tabular-nums text-slate-900 dark:text-white">
                  {overall.completed}/{totalLessons} lessons
                </span>
              </div>
              <Progress value={overall.percentage} className="mt-2 h-2" />
              <p className="mt-1 text-right text-xs text-slate-500 dark:text-slate-500">
                {overall.percentage}% complete
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Sequence Architecture */}
      <section className="border-b py-12 md:py-16">
        <div className="container">
          <h2 className="mb-8 text-center text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Architectural Logic
          </h2>
          <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-2 text-sm">
            {[
              'Who is knowing',
              'How we know',
              'What the mind does',
              'How collectives construct reality',
              'How power operates',
              'How systems behave',
              'The material form of the present',
              'The information envelope',
              'Navigation & Synthesis',
            ].map((step, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="rounded-full bg-purple-100 px-3 py-1 font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                  {i + 1}. {step}
                </span>
                {i < 8 && (
                  <ArrowRight className="size-3 text-slate-400 dark:text-slate-600" />
                )}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-12 md:py-16">
        <div className="container">
          <h2 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            The 8 Modules
          </h2>
          <p className="mb-8 text-slate-600 dark:text-slate-400">
            {totalLessons} lessons · ~10 weeks · Daily micro-sessions
          </p>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {CURRICULUM.map((mod) => {
              const Icon = MODULE_ICONS[mod.icon] || BookOpen;
              const mp = getModuleProgress(mod.id, progress);
              return (
                <Link key={mod.id} to={`/module/${mod.id}`}>
                  <Card
                    className={cn(
                      'group h-full transition-all duration-300 hover:shadow-md hover:-translate-y-1',
                      mp.percentage === 100 && 'ring-2 ring-emerald-200 dark:ring-emerald-800'
                    )}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <span
                          className="inline-flex size-10 items-center justify-center rounded-lg text-white"
                          style={{ backgroundColor: mod.color }}
                        >
                          <Icon className="size-5" />
                        </span>
                        <Badge variant="secondary" className="font-mono">
                          M{mod.number}
                        </Badge>
                      </div>
                      <CardTitle className="mt-3 text-base leading-snug">
                        {mod.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-3 text-xs leading-relaxed">
                        {mod.theme}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500 dark:text-slate-400">
                            {mp.completed}/{mod.lessons.length} lessons
                          </span>
                          <span className="font-mono font-medium tabular-nums text-slate-700 dark:text-slate-300">
                            {mp.percentage}%
                          </span>
                        </div>
                        <Progress value={mp.percentage} className="h-1.5" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vectors */}
      <section className="border-t bg-slate-50 py-12 dark:bg-slate-900/50 md:py-16">
        <div className="container">
          <h2 className="mb-8 text-center text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Four Integrated Vectors
          </h2>
          <div className="mx-auto grid max-w-3xl gap-6 sm:grid-cols-2">
            {[
              { label: 'Critical / Structural', desc: 'Race, gender, coloniality, class as constitutive, not additive' },
              { label: 'Materialist / Formal', desc: 'Thermodynamics, complexity science, cognitive architecture' },
              { label: 'Phenomenological / Contemplative', desc: 'First-person inquiry as rigorous method, not supplement' },
              { label: 'Ecological / More-than-human', desc: 'The living world as analytical actor, not backdrop' },
            ].map((v) => (
              <div
                key={v.label}
                className="rounded-lg border bg-white p-5 dark:border-slate-800 dark:bg-slate-950"
              >
                <p className="font-semibold text-slate-900 dark:text-white">{v.label}</p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 text-center text-sm text-slate-500 dark:text-slate-500">
        <div className="container">
          <p>
            Curriculum designed for daily micro-practice. 5-10 minutes per session.{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-dotted underline-offset-4 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Vibed with Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
